import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { IScannedCodeValue, ScannedCodeSource } from './interfaces';

@Injectable({
    providedIn: 'root'
})
export class TimeBufferService {
    private state: ITimeBufferState = {
        items: [],
        maxItems: 1000,
        checkTimerId: 0,
        options: this.getDefaultOptions()
    };

    private candidates$ = new Subject<IScannedCodeValue>();

    getCandidatesObservable(): Observable<IScannedCodeValue> {
        return this.candidates$.asObservable();
    }

    getBufferedItems(): ITimeBufferItem[] {
        return this.state.items;
    }

    getOptions(): ITimeBufferOptions {
        return this.state.options;
    }

    setOptions(options: ITimeBufferOptions): void {
        this.state.options = options;
    }

    addValue(value: string): void {
        this.state.items.push({
            addedAt: this.getNow(),
            value: value
        });
        // Keep the buffer with reasonable length
        if (this.state.items.length > this.state.maxItems) {
            // Negative start slice parameter means the item from the end
            // Providing the maxItems will slice the last maxItems count items which are the most recent
            this.state.items = this.state.items.slice(-this.state.maxItems);
        }
        // Check after specified time for buffer value
        this.startCheckTimer();
    }

    getNow(): number {
        return Date.now();
    }

    startCheckTimer(): void {
        clearTimeout(this.state.checkTimerId);
        this.state.checkTimerId = window.setTimeout(() => this.processCandidates(), this.state.options.checkTimerTimeout);
    }

    processCandidates(): void {
        // Check if we have values
        const lastCandidates = this.getLastCandidates(this.state.items, this.state.options.maxMsBetweenCharacters);
        this.state.items = [];
        if (lastCandidates.length >= this.state.options.itemsMinLength) {
            // Emit the candidates
            const candidatesValues = lastCandidates.map(x => x.value);
            let text = candidatesValues.join('');
            if (this.state.options.trimWhiteSpace) {
                text = text.trim();
            }
            if (text.length >= this.state.options.itemsMinLength) {
                const { minGap, maxGap } = this.getMinAndMaxGap(lastCandidates.map(x => x.addedAt));
                const candidateValue: IScannedCodeValue = {
                    value: text,
                    duration: lastCandidates[lastCandidates.length - 1].addedAt - lastCandidates[0].addedAt,
                    minGap: minGap,
                    maxGap: maxGap,
                    // TODO: What if we have another type of scanning which uses this service ?
                    source: ScannedCodeSource.handheldScanner
                };
                this.candidates$.next(candidateValue);
            }
        }
    }

    getMinAndMaxGap(timestamps: number[]): { minGap: number; maxGap: number } {
        if (timestamps.length <= 1) {
            return { minGap: 0, maxGap: 0 };
        }
        const firstDiff = Math.abs(timestamps[0] - timestamps[1]);
        let minGap = firstDiff;
        let maxGap = firstDiff;
        for (let i = 0; i < timestamps.length - 2; i++) {
            const diff = Math.abs(timestamps[i] - timestamps[i + 1]);
            if (diff < minGap) {
                minGap = diff;
            }
            if (diff > maxGap) {
                maxGap = diff;
            }
        }
        return {
            minGap: minGap,
            maxGap: maxGap
        };
    }

    getLastCandidates(items: ITimeBufferItem[], maxMsBetweenCharacters: number): ITimeBufferItem[] {
        const candidates: ITimeBufferItem[] = [];
        let bigGapFound = false;
        for (let i = items.length - 1; i >= 1; i--) {
            candidates.push(items[i]);
            const diff = items[i].addedAt - items[i - 1].addedAt;
            if (diff > maxMsBetweenCharacters) {
                bigGapFound = true;
                break;
            }
        }
        if (!bigGapFound) {
            // If no big gap was found, we need to add the first item, because it was not added inside the for loop
            candidates.push(items[0]);
        }
        // The items were added from last to first - we must reverse the ordering to appear as the original order
        candidates.reverse();
        return candidates;
    }

    getDefaultOptions(): ITimeBufferOptions {
        return {
            checkTimerTimeout: 400,
            itemsMinLength: 24,
            trimWhiteSpace: true,
            maxMsBetweenCharacters: 100
        };
    }
}

export interface ITimeBufferState {
    items: ITimeBufferItem[];
    maxItems: number;
    checkTimerId: number;
    options: ITimeBufferOptions;
}

export interface ITimeBufferItem {
    addedAt: number;
    value: string;
}

export interface ITimeBufferOptions {
    checkTimerTimeout: number;
    itemsMinLength: number;
    trimWhiteSpace: boolean;
    maxMsBetweenCharacters: number;
}
